import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { REFERRAL_PRIORITIES, type Facility, type LabObservation, type LabOrder, type Page, type ReferralPriority } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const STATUSES = ['ORDERED', 'SENT_TO_LAB', 'RESULT_READY', 'CANCELLED'];

/**
 * Lab orders/results — packages/modules/m24-ehealth/src/routes/labs.ts.
 * Orders are submitted to the external lab partner as an HL7-FHIR
 * ServiceRequest at creation time; results come back DiagnosticReport-shaped.
 * Sensitive test classes (HIV, hepatitis, STI panel, psych eval — see
 * src/service/labFhir.ts's SENSITIVE_TEST_CODES) stay PENDING_RELEASE until
 * a clinician explicitly releases or withholds them.
 */
export function Labs() {
  const { persona } = useAuth();
  const isClinician = persona?.roles.includes('CLINICIAN') ?? false;
  const canView = (isClinician || persona?.roles.includes('LAB_PARTNER') || persona?.roles.includes('RECEPTION')) ?? false;
  const isLabPartner = persona?.roles.includes('LAB_PARTNER') ?? false;

  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<LabOrder>>(`/api/v1/m24/lab-orders?${qs.toString()}`);

  return (
    <div>
      <PageHeader
        title="Lab Orders"
        subtitle="HL7-FHIR-shaped orders/results with the external lab partner; sensitive classes are clinician release-gated."
        actions={
          isClinician ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Order test
            </button>
          ) : undefined
        }
      />

      {!canView ? (
        <div className="mb-4">
          <ErrorNote message="Viewing lab orders requires CLINICIAN, LAB_PARTNER or RECEPTION." />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-48" placeholder="Search NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No lab orders match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Patient (NIN)</th>
                <th className="table-head">Test</th>
                <th className="table-head">Priority</th>
                <th className="table-head">Status</th>
                <th className="table-head">Result</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((o) => (
                <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(o.id)}>
                  <td className="table-cell font-medium">{o.nin}</td>
                  <td className="table-cell">
                    {o.testName} <span className="text-xs text-slate-400">({o.testCode})</span>
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={o.priority} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="table-cell">
                    {o.result ? (
                      <span className="flex items-center gap-1.5">
                        <StatusBadge status={o.result.releaseStatus} />
                        {o.result.sensitiveClass ? <span className="text-xs text-rose-500" title="Sensitive class">🔒</span> : null}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="table-cell text-right">
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setDetailId(o.id)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateLabOrderModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}

      {detailId ? (
        <LabOrderDetailModal id={detailId} isClinician={isClinician} isLabPartner={isLabPartner} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function CreateLabOrderModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const facilitiesQ = useApiQuery<Page<Facility>>('/api/v1/m24/facilities?limit=100');
  const [nin, setNin] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [testCode, setTestCode] = useState('');
  const [testName, setTestName] = useState('');
  const [priority, setPriority] = useState<ReferralPriority>('ROUTINE');
  const [referralId, setReferralId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m24/lab-orders', {
        nin,
        facilityId,
        testCode: testCode.toUpperCase(),
        testName,
        priority,
        referralId: referralId.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to order test');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Order a lab test" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Patient NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="e.g. SC870512033" />
        </Field>
        <Field label="Ordering facility">
          <select className="field-input" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
            <option value="">Select a facility…</option>
            {(facilitiesQ.data?.items ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Test code">
            <input className="field-input" value={testCode} onChange={(e) => setTestCode(e.target.value)} placeholder="e.g. FBC, HIV-AB" />
          </Field>
          <Field label="Priority">
            <select className="field-input" value={priority} onChange={(e) => setPriority(e.target.value as ReferralPriority)}>
              {REFERRAL_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Test name">
          <input className="field-input" value={testName} onChange={(e) => setTestName(e.target.value)} placeholder="e.g. Full Blood Count" />
        </Field>
        <Field label="Linked referral id (optional)">
          <input className="field-input" value={referralId} onChange={(e) => setReferralId(e.target.value)} placeholder="referral id…" />
        </Field>
        <p className="text-[11px] text-slate-400">
          Test codes HIV-AB, HIV-VL, HEP-B-SAG, HEP-C-AB, STI-PANEL and PSYCH-EVAL are treated as a sensitive class — their result stays hidden
          from the patient until a clinician releases it.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || nin.trim().length < 5 || !facilityId || !testCode.trim() || !testName.trim()}
          >
            {busy ? 'Ordering…' : 'Submit order'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function LabOrderDetailModal({
  id,
  isClinician,
  isLabPartner,
  onClose,
  onChanged,
}: {
  id: string;
  isClinician: boolean;
  isLabPartner: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<LabOrder>(`/api/v1/m24/lab-orders/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resultSummary, setResultSummary] = useState('');
  const [observations, setObservations] = useState<LabObservation[]>([]);
  const [obsCode, setObsCode] = useState('');
  const [obsDisplay, setObsDisplay] = useState('');
  const [obsValue, setObsValue] = useState('');
  const [obsUnit, setObsUnit] = useState('');
  const [withholdReason, setWithholdReason] = useState('');
  const [showWithhold, setShowWithhold] = useState(false);

  const order = detailQ.data;

  const pushResult = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m24/lab-orders/${id}/result`, { resultSummary: resultSummary || undefined, observations: observations.length > 0 ? observations : undefined });
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record result');
    } finally {
      setBusy(false);
    }
  };

  const releaseOrWithhold = async (action: 'release' | 'withhold', reason?: string) => {
    if (!order?.result) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m24/lab-results/${order.result.id}/${action}`, action === 'withhold' ? { reason } : undefined);
      detailQ.reload();
      onChanged();
      setShowWithhold(false);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `Could not ${action} result`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Lab order" onClose={onClose} wide>
      {detailQ.loading || !order ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={order.priority} />
            <StatusBadge status={order.status} />
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Patient NIN</dt>
              <dd className="font-medium">{order.nin}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Test</dt>
              <dd className="font-medium">
                {order.testName} ({order.testCode})
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">External lab ref</dt>
              <dd className="font-mono text-xs">{order.externalLabRef ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Ordered</dt>
              <dd className="font-medium">{formatDateTime(order.createdAt)}</dd>
            </div>
          </dl>

          {!order.result && isLabPartner ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Push result (external lab partner)</div>
              <Field label="Result summary" className="mb-2">
                <textarea className="field-input" rows={2} value={resultSummary} onChange={(e) => setResultSummary(e.target.value)} />
              </Field>
              <div className="mb-2 grid grid-cols-4 gap-1.5">
                <input className="field-input" placeholder="Code" value={obsCode} onChange={(e) => setObsCode(e.target.value)} />
                <input className="field-input" placeholder="Display" value={obsDisplay} onChange={(e) => setObsDisplay(e.target.value)} />
                <input className="field-input" placeholder="Value" value={obsValue} onChange={(e) => setObsValue(e.target.value)} />
                <div className="flex gap-1">
                  <input className="field-input" placeholder="Unit" value={obsUnit} onChange={(e) => setObsUnit(e.target.value)} />
                  <button
                    className="btn-secondary shrink-0 !px-2"
                    disabled={!obsCode.trim() || !obsDisplay.trim() || !obsValue.trim()}
                    onClick={() => {
                      const value: string | number = Number.isNaN(Number(obsValue)) ? obsValue : Number(obsValue);
                      setObservations((obs) => [...obs, { code: obsCode, display: obsDisplay, value, unit: obsUnit || undefined }]);
                      setObsCode('');
                      setObsDisplay('');
                      setObsValue('');
                      setObsUnit('');
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
              {observations.length > 0 ? (
                <ul className="mb-2 space-y-1 text-xs text-slate-600">
                  {observations.map((o, i) => (
                    <li key={i}>
                      {o.display}: {o.value} {o.unit ?? ''}
                    </li>
                  ))}
                </ul>
              ) : null}
              <button className="btn-primary" onClick={pushResult} disabled={busy}>
                {busy ? 'Submitting…' : 'Submit result'}
              </button>
            </div>
          ) : null}

          {order.result ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Result</div>
                <span className="flex items-center gap-1.5">
                  <StatusBadge status={order.result.releaseStatus} />
                  {order.result.sensitiveClass ? <span className="text-xs font-semibold text-rose-500">Sensitive class</span> : null}
                </span>
              </div>
              <p className="mb-2 text-slate-700">{order.result.resultSummary || 'No summary text provided.'}</p>
              <details className="text-xs text-slate-400">
                <summary className="cursor-pointer select-none">Raw FHIR DiagnosticReport</summary>
                <pre className="mt-1 max-h-48 overflow-auto rounded bg-slate-50 p-2">{JSON.stringify(order.result.fhirPayload, null, 2)}</pre>
              </details>

              {order.result.releaseStatus === 'PENDING_RELEASE' && isClinician ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => releaseOrWithhold('release')} disabled={busy}>
                    Release to patient
                  </button>
                  <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowWithhold((s) => !s)} disabled={busy}>
                    Withhold
                  </button>
                </div>
              ) : null}
              {order.result.withheldReason ? <p className="mt-2 text-xs text-rose-600">Withheld: {order.result.withheldReason}</p> : null}
              {showWithhold ? (
                <div className="mt-2 flex gap-2">
                  <input className="field-input" placeholder="Reason (min 3 chars)…" value={withholdReason} onChange={(e) => setWithholdReason(e.target.value)} />
                  <button
                    className="btn-danger shrink-0 !px-2.5 !py-1 text-xs"
                    disabled={withholdReason.trim().length < 3 || busy}
                    onClick={() => releaseOrWithhold('withhold', withholdReason)}
                  >
                    Confirm withhold
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-slate-400">No result recorded yet.</p>
          )}
        </div>
      )}
    </Modal>
  );
}
